import { useState } from 'react';
import { Popover } from 'radix-ui';
import { CloudSun, RefreshCw, X } from 'lucide-react';
import { IconButton } from '@/components/ui/primitives';
import { cn } from '@/lib/cn';
import { WEATHER } from '../constants';
import { useLoader } from '../store';
import { WEATHER_NOTES, weatherSource, type WeatherTimeMode } from '../weather/imagery';
import { useWeatherOverlay } from '../weather/store';

const groups = [...new Set(Object.values(WEATHER.LAYERS).map((l) => l.group))];
const quick = [
  { key: 'goes_ir', name: 'Infrared · US' },
  { key: 'viirs_true', name: 'Clouds · global' },
  { key: 'nexrad', name: 'Radar · US' },
];
const control = 'w-full rounded-control border border-hairline-strong bg-inset px-2 py-1.5 text-xs text-ink';

export function WeatherMenu() {
  const key = useLoader((s) => s.weatherLayer);
  const opacity = useLoader((s) => s.weatherOpacity);
  const setView = useLoader((s) => s.setView);
  const state = useWeatherOverlay();
  const [failedLegend, setFailedLegend] = useState<string | null>(null);
  const config = key ? WEATHER.LAYERS[key] : null;
  const legend = state.metadata?.legendUrl;
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <IconButton label="Weather overlays" className={key ? 'bg-accent/10 text-accent' : undefined}>
          <CloudSun className="h-4 w-4" />
        </IconButton>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          collisionPadding={10}
          aria-label="Weather overlays"
          className="z-50 w-[320px] max-w-[calc(100vw-20px)] overflow-y-auto rounded-panel border border-hairline-strong bg-raised p-4 text-xs shadow-elev-2"
          style={{ maxHeight: 'min(650px, var(--radix-popover-content-available-height))' }}
        >
          <div className="mb-3 flex items-center justify-between">
            <span className="font-medium text-ink">Weather & environment</span>
            <Popover.Close asChild>
              <IconButton label="Close weather overlays">
                <X className="h-3.5 w-3.5" />
              </IconButton>
            </Popover.Close>
          </div>
          <div className="mb-3 grid grid-cols-3 gap-1">
            {quick.map((item) => (
              <button
                key={item.key}
                type="button"
                aria-pressed={key === item.key}
                onClick={() => setView({ weatherLayer: item.key })}
                className={cn(
                  'cursor-pointer rounded-control border px-1 py-2 text-[10px]',
                  key === item.key
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-hairline text-ink-2 hover:bg-overlay',
                )}
              >
                {item.name}
              </button>
            ))}
          </div>
          <label className="mb-1 block text-ink-3" htmlFor="weather-layer">
            Imagery layer
          </label>
          <select
            id="weather-layer"
            aria-label="Weather layer"
            value={key ?? ''}
            onChange={(e) => setView({ weatherLayer: e.target.value || null })}
            className={control}
          >
            <option value="">Off</option>
            {groups.map((group) => (
              <optgroup key={group} label={group}>
                {Object.entries(WEATHER.LAYERS)
                  .filter(([, l]) => l.group === group)
                  .map(([id, l]) => (
                    <option key={id} value={id}>
                      {l.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
          {key && config ? (
            <>
              <div className="mt-3 flex items-center justify-between text-ink-2">
                <label htmlFor="weather-opacity">Opacity</label>
                <span>{Math.round(opacity * 100)}%</span>
              </div>
              <input
                id="weather-opacity"
                aria-label="Weather layer opacity"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={opacity}
                onChange={(e) => setView({ weatherOpacity: Number(e.target.value) })}
                className="mt-1 w-full accent-[var(--color-accent)]"
              />
              <label htmlFor="weather-time" className="mb-1 mt-3 block text-ink-3">
                Imagery time
              </label>
              <select
                id="weather-time"
                value={state.mode}
                onChange={(e) => state.setMode(e.target.value as WeatherTimeMode)}
                className={control}
              >
                <option value="latest">Latest available</option>
                <option value="timeline">Follow Loader date · UTC</option>
              </select>
              <div
                className="mt-3 rounded-control border border-hairline bg-inset p-2.5 leading-relaxed"
                role="status"
                data-testid="weather-status"
              >
                <div className="font-medium text-ink">{weatherSource(key)}</div>
                <div className="text-ink-2">
                  {config.gibs ? 'Global coverage · daily composites' : 'Regional coverage · North America'}
                </div>
                {state.phase === 'loading' && <p className="mt-1 text-ink-2">Checking available imagery…</p>}
                {state.request?.time && (
                  <p className="mt-1 text-accent">Imagery date: {state.request.time} UTC</p>
                )}
                {state.phase === 'available' && !state.request?.time && (
                  <p className="mt-1 text-ink-2">
                    Latest image; acquisition time is not published by this service.
                  </p>
                )}
                {state.message && <p className="mt-1 text-warning">{state.message}</p>}
                {state.tileError && (
                  <p className="mt-1 text-warning">Some imagery could not load. Refresh to retry.</p>
                )}
                {state.checkedAt && (
                  <p className="mt-1 text-[10px] text-ink-3">
                    Checked {new Date(state.checkedAt).toISOString().slice(11, 16)} UTC · refreshes every 10
                    min
                  </p>
                )}
              </div>
              {legend && failedLegend !== legend && (
                <div className="mt-3 overflow-hidden rounded-control bg-white p-1">
                  <img
                    src={legend}
                    alt={`${config.name} legend and units`}
                    className="mx-auto max-h-32 max-w-full object-contain"
                    onError={() => setFailedLegend(legend)}
                  />
                </div>
              )}
              {WEATHER_NOTES[key] && <p className="mt-2 leading-relaxed text-ink-2">{WEATHER_NOTES[key]}</p>}
              <div className="mt-3 flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={state.refresh}
                  disabled={state.phase === 'loading'}
                  className="flex cursor-pointer items-center gap-1.5 rounded-control border border-hairline px-2 py-1.5 text-ink-2 hover:bg-overlay disabled:opacity-40"
                >
                  <RefreshCw className="h-3 w-3" /> Refresh imagery
                </button>
                <button
                  type="button"
                  onClick={() => setView({ weatherLayer: null })}
                  className="cursor-pointer px-2 py-1.5 text-ink-3 hover:text-ink"
                >
                  Turn off
                </button>
              </div>
            </>
          ) : (
            <p className="mt-3 leading-relaxed text-ink-3">
              Choose an observation layer to place it over your current map. US layers are visible over North
              America.
            </p>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
